import { useEffect, useRef, useState } from "react";
import { useAuth } from "@clerk/react";
import { MapPin } from "lucide-react";
import { Input } from "@/components/ui/input";
import { makeApi } from "@/lib/api";

// Free-text address field with as-you-type suggestions (city + state or full street address).
export const AddressInput = ({ id, value, onChange, placeholder, testId }) => {
  const { getToken } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const typed = useRef(false);
  const seq = useRef(0);

  useEffect(() => {
    const q = (value || "").trim();
    if (!typed.current || q.length < 3) { setItems([]); return undefined; }
    const mine = ++seq.current;
    const t = setTimeout(async () => {
      try {
        const { data } = await makeApi(getToken).get("/geo/suggest", { params: { q } });
        if (mine === seq.current) { setItems(data); setActive(-1); setOpen(true); }
      } catch { /* suggestions are optional */ }
    }, 300);
    return () => clearTimeout(t);
  }, [value, getToken]);

  const pick = (text) => { typed.current = false; onChange(text); setOpen(false); setItems([]); };

  const onKeyDown = (e) => {
    if (!open || items.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => (a + 1) % items.length); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => (a <= 0 ? items.length - 1 : a - 1)); }
    else if (e.key === "Enter" && active >= 0) { e.preventDefault(); pick(items[active]); }
    else if (e.key === "Escape") setOpen(false);
  };

  return (
    <div className="relative">
      <Input
        id={id}
        value={value}
        autoComplete="off"
        placeholder={placeholder}
        onChange={(e) => { typed.current = true; onChange(e.target.value); }}
        onKeyDown={onKeyDown}
        onFocus={() => items.length && setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        role="combobox"
        aria-expanded={open && items.length > 0}
        data-testid={testId}
      />
      {open && items.length > 0 && (
        <ul className="absolute z-50 mt-1 w-full rounded-lg border border-slate-200 bg-white shadow-lg overflow-hidden" role="listbox" data-address-suggestions data-testid={`${testId}-suggestions`}>
          {items.map((t, i) => (
            <li key={t}>
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); pick(t); }}
                className={`w-full flex items-start gap-2 px-3 py-2.5 text-left text-sm ${i === active ? "bg-blue-50" : "hover:bg-slate-50"}`}
                data-testid={`${testId}-suggestion-${i}`}
              >
                <MapPin className="w-4 h-4 mt-0.5 shrink-0 text-slate-400" />
                <span>{t}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export const ADDRESS_HINT = "Type a city and state (Dallas, TX) or a full street address, then pick a suggestion.";
